import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../axios/axiosInstance";

const BASE = "/api/waba/automation";

// ── Basic Replies ──────────────────────────────────────
export const fetchBasicReplies = createAsyncThunk("wabaAutomation/fetchBasicReplies", async (payload, { rejectWithValue, getState }) => {
  const state = getState().wabaAutomation;
  if (!payload?.forceRefresh && state.basicRepliesCachedAt) {
    return { data: state.basicReplies, fromCache: true };
  }
  try { return { data: (await api.get(`${BASE}/basic-replies`)).data.data, fromCache: false }; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const createBasicReply = createAsyncThunk("wabaAutomation/createBasicReply", async (payload, { rejectWithValue }) => {
  try { return (await api.post(`${BASE}/basic-replies`, payload)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const updateBasicReply = createAsyncThunk("wabaAutomation/updateBasicReply", async ({ id, ...payload }, { rejectWithValue }) => {
  try { return (await api.put(`${BASE}/basic-replies/${id}`, payload)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const deleteBasicReply = createAsyncThunk("wabaAutomation/deleteBasicReply", async (id, { rejectWithValue }) => {
  try { await api.delete(`${BASE}/basic-replies/${id}`); return id; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const toggleBasicReply = createAsyncThunk("wabaAutomation/toggleBasicReply", async (id, { rejectWithValue }) => {
  try { return (await api.patch(`${BASE}/basic-replies/${id}/toggle`)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});

// ── Flows ──────────────────────────────────────────────
export const fetchFlows = createAsyncThunk("wabaAutomation/fetchFlows", async (payload, { rejectWithValue, getState }) => {
  const state = getState().wabaAutomation;
  if (!payload?.forceRefresh && state.flowsCachedAt) {
    return { data: state.flows, fromCache: true };
  }
  try { return { data: (await api.get(`${BASE}/flows`)).data.data, fromCache: false }; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const createFlow = createAsyncThunk("wabaAutomation/createFlow", async (payload, { rejectWithValue }) => {
  try { return (await api.post(`${BASE}/flows`, payload)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const updateFlow = createAsyncThunk("wabaAutomation/updateFlow", async ({ id, ...payload }, { rejectWithValue }) => {
  try { return (await api.put(`${BASE}/flows/${id}`, payload)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const deleteFlow = createAsyncThunk("wabaAutomation/deleteFlow", async (id, { rejectWithValue }) => {
  try { await api.delete(`${BASE}/flows/${id}`); return id; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const toggleFlow = createAsyncThunk("wabaAutomation/toggleFlow", async (id, { rejectWithValue }) => {
  try { return (await api.patch(`${BASE}/flows/${id}/toggle`)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const importFlow = createAsyncThunk("wabaAutomation/importFlow", async (payload, { rejectWithValue }) => {
  try { return (await api.post(`${BASE}/flows/import`, payload)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});
export const duplicateFlow = createAsyncThunk("wabaAutomation/duplicateFlow", async (id, { rejectWithValue }) => {
  try { return (await api.post(`${BASE}/flows/${id}/duplicate`)).data.data; }
  catch (e) { return rejectWithValue(e.response?.data?.message); }
});

const slice = createSlice({
  name: "wabaAutomation",
  initialState: {
    basicReplies: [],
    flows: [],
    loading: false,
    error: null,
    flowsCachedAt: null,
    basicRepliesCachedAt: null,
  },
  reducers: {},
  extraReducers: (b) => {
    const pending  = (s) => { s.loading = true; s.error = null; };
    const rejected = (s, a) => { s.loading = false; s.error = a.payload; };

    b.addCase(fetchBasicReplies.pending, pending)
     .addCase(fetchBasicReplies.fulfilled, (s, a) => {
       s.loading = false;
       if (!a.payload.fromCache) { s.basicReplies = a.payload.data; s.basicRepliesCachedAt = Date.now(); }
     })
     .addCase(fetchBasicReplies.rejected, rejected)

     .addCase(createBasicReply.fulfilled, (s, a) => { s.basicReplies.unshift(a.payload); })
     .addCase(updateBasicReply.fulfilled, (s, a) => {
       const i = s.basicReplies.findIndex(r => r._id === a.payload._id);
       if (i !== -1) s.basicReplies[i] = a.payload;
     })
     .addCase(deleteBasicReply.fulfilled, (s, a) => { s.basicReplies = s.basicReplies.filter(r => r._id !== a.payload); })
     .addCase(toggleBasicReply.fulfilled, (s, a) => {
       const i = s.basicReplies.findIndex(r => r._id === a.payload._id);
       if (i !== -1) s.basicReplies[i] = a.payload;
     })

     .addCase(fetchFlows.pending, pending)
     .addCase(fetchFlows.fulfilled, (s, a) => {
       s.loading = false;
       if (!a.payload.fromCache) { s.flows = a.payload.data; s.flowsCachedAt = Date.now(); }
     })
     .addCase(fetchFlows.rejected, rejected)

     .addCase(createFlow.fulfilled, (s, a) => { s.flows.unshift(a.payload); })
     .addCase(updateFlow.fulfilled, (s, a) => {
       const i = s.flows.findIndex(f => f._id === a.payload._id);
       if (i !== -1) s.flows[i] = a.payload;
     })
     .addCase(deleteFlow.fulfilled, (s, a) => { s.flows = s.flows.filter(f => f._id !== a.payload); })
     .addCase(toggleFlow.fulfilled, (s, a) => {
       const i = s.flows.findIndex(f => f._id === a.payload._id);
       if (i !== -1) s.flows[i] = a.payload;
     })
     .addCase(importFlow.fulfilled, (s, a) => { s.flows.unshift(a.payload); })
     .addCase(duplicateFlow.fulfilled, (s, a) => { s.flows.unshift(a.payload); });
  },
});

export default slice.reducer;
